import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import { useDishRequirements, useEquipmentItems, useMenuItems, useSaveDishRequirements } from '@/api/hooks'
import type { EquipmentItem, MenuItem as MenuItemType } from '@/api/types'
import { ErrorBanner, formatNumber, InlineSpinner } from '@/components/common'

type Line = {
  equipmentItemId: string
  name: string
  purpose: 'TRANSPORT' | 'SERVICE' | 'COOKING'
  serviceStyle: '' | 'BUFFET' | 'LEAF'
  quantity: string
  perGuests: string
  alsoServes: boolean
}

type Ingredient = {
  ingredientItemId: string
  name: string
  quantity: string
  unit: string
  perGuests: string
}

/** The four questions a dish is asked, in the order the office answers them. */
const SECTIONS: { purpose: Line['purpose']; style: Line['serviceStyle']; label: string }[] = [
  { purpose: 'TRANSPORT', style: 'BUFFET', label: 'Travels in — buffet' },
  { purpose: 'TRANSPORT', style: 'LEAF', label: 'Travels in — leaf' },
  { purpose: 'SERVICE', style: 'BUFFET', label: 'Served with — buffet' },
  { purpose: 'SERVICE', style: 'LEAF', label: 'Served with — leaf' },
  { purpose: 'COOKING', style: '', label: 'Cooked in — live counter' },
]

/**
 * What one dish needs: its vessels, its cutlery, and what a live counter cooks
 * from.
 *
 * <p>Buffet and leaf are asked separately because they differ - the same sambar
 * travels in a can either way, but is served from a chafing dish with a karandi
 * on a buffet and from a vali with a karandi down a leaf line. Cutlery is not a
 * separate question: a karandi is an equipment item like a vessel is, so it
 * goes on the service line beside what it serves from, which is also how it is
 * counted back at the gate.</p>
 */
export function DishRequirementsDialog({
  item,
  onClose,
}: {
  item: MenuItemType | null
  onClose: () => void
}) {
  const { data, isLoading } = useDishRequirements(item?.id)
  const save = useSaveDishRequirements()
  const { data: vesselItems } = useEquipmentItems('VESSEL')
  const [ingredientSearch, setIngredientSearch] = useState('')
  const { data: catalogue } = useMenuItems({ q: ingredientSearch || undefined, size: 30 })

  const [lines, setLines] = useState<Line[]>([])
  const [ingredients, setIngredients] = useState<Ingredient[]>([])

  // The entry line, one per section, cleared after each add.
  const [picked, setPicked] = useState<Record<string, EquipmentItem | null>>({})
  const [quantity, setQuantity] = useState<Record<string, string>>({})
  const [ingredientPicked, setIngredientPicked] = useState<MenuItemType | null>(null)
  const [ingredientQty, setIngredientQty] = useState('1')

  useEffect(() => {
    if (!data) return
    setLines(data.vessels.map((vessel) => ({
      equipmentItemId: vessel.equipmentItemId,
      name: vessel.itemName,
      purpose: vessel.purpose,
      serviceStyle: vessel.serviceStyle ?? '',
      quantity: String(vessel.quantity),
      perGuests: vessel.perGuests ? String(vessel.perGuests) : '',
      alsoServes: vessel.alsoServes,
    })))
    setIngredients(data.ingredients.map((line) => ({
      ingredientItemId: line.ingredientItemId,
      name: line.itemName,
      quantity: String(line.quantity),
      unit: line.unit,
      perGuests: line.perGuests ? String(line.perGuests) : '',
    })))
  }, [data?.menuItemId, data?.vessels.length, data?.ingredients.length])

  if (!item) return null

  const key = (section: typeof SECTIONS[number]) => `${section.purpose}-${section.style}`

  const addTo = (section: typeof SECTIONS[number]) => {
    const chosen = picked[key(section)]
    if (!chosen) return
    setLines((current) => {
      const already = current.some((line) =>
        line.equipmentItemId === chosen.id
        && line.purpose === section.purpose
        && line.serviceStyle === section.style)
      if (already) return current
      return [...current, {
        equipmentItemId: chosen.id,
        name: chosen.name,
        purpose: section.purpose,
        serviceStyle: section.style,
        quantity: quantity[key(section)] || '1',
        perGuests: '',
        alsoServes: false,
      }]
    })
    setPicked((current) => ({ ...current, [key(section)]: null }))
    setQuantity((current) => ({ ...current, [key(section)]: '1' }))
  }

  const submit = () =>
    save.mutate({
      itemId: item.id,
      body: {
        vessels: lines.map((line) => ({
          purpose: line.purpose,
          serviceStyle: line.serviceStyle || undefined,
          equipmentItemId: line.equipmentItemId,
          quantity: Number(line.quantity || 1),
          perGuests: line.perGuests ? Number(line.perGuests) : undefined,
          alsoServes: line.alsoServes,
        })),
        ingredients: ingredients.map((line) => ({
          ingredientItemId: line.ingredientItemId,
          quantity: Number(line.quantity || 1),
          unit: line.unit,
          perGuests: line.perGuests ? Number(line.perGuests) : undefined,
        })),
      },
    }, { onSuccess: onClose })

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {item.name}
        {item.liveCounter ? (
          <Chip size="small" color="secondary" label="Live counter" sx={{ ml: 1 }} />
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={save.error} />
        {isLoading ? <InlineSpinner /> : null}

        {(vesselItems ?? []).length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nothing in the store is marked as a vessel yet. Add the cans, chafing dishes, valis and
            karandis under <strong>Gate passes → Store items</strong> first — a dish can only be
            mapped to what the business owns.
          </Alert>
        ) : null}

        {SECTIONS.filter((section) => section.purpose !== 'COOKING' || item.liveCounter)
          .map((section) => {
            const mine = lines.filter((line) =>
              line.purpose === section.purpose && line.serviceStyle === section.style)
            return (
              <div key={key(section)}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  {section.label}
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                  <Autocomplete
                    size="small"
                    options={vesselItems ?? []}
                    value={picked[key(section)] ?? null}
                    onChange={(_event, value) =>
                      setPicked((current) => ({ ...current, [key(section)]: value }))
                    }
                    getOptionLabel={(option) => option.name}
                    sx={{ flex: 1, minWidth: 220 }}
                    renderInput={(params) => <TextField {...params} label="Vessel or cutlery" />}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="How many"
                    value={quantity[key(section)] ?? '1'}
                    onChange={(event) =>
                      setQuantity((current) => ({ ...current, [key(section)]: event.target.value }))
                    }
                    sx={{ width: 120 }}
                  />
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addTo(section)}
                    disabled={!picked[key(section)]}
                  >
                    Add
                  </Button>
                </Stack>

                {mine.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing mapped.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">How many</TableCell>
                        <TableCell align="right">Per guests</TableCell>
                        {section.purpose === 'TRANSPORT' ? (
                          <TableCell align="center">Also served from</TableCell>
                        ) : null}
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mine.map((line) => (
                        <TableRow key={`${key(section)}-${line.equipmentItemId}`} hover>
                          <TableCell>{line.name}</TableCell>
                          <TableCell align="right">{formatNumber(Number(line.quantity))}</TableCell>
                          <TableCell align="right">
                            {/* Blank is a flat quantity: one karandi is one
                                karandi whether forty eat or four hundred. */}
                            <TextField
                              size="small"
                              type="number"
                              placeholder="Flat"
                              value={line.perGuests}
                              onChange={(event) =>
                                setLines((current) => current.map((candidate) =>
                                  candidate === line
                                    ? { ...candidate, perGuests: event.target.value }
                                    : candidate))
                              }
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          {/* One vessel doing both jobs. Listing the same can
                              under travels-in and again under served-with would
                              put two on the gate pass when one leaves the
                              store, and the second comes back short at the
                              count because it never existed. */}
                          {section.purpose === 'TRANSPORT' ? (
                            <TableCell align="center">
                              <Checkbox
                                size="small"
                                checked={line.alsoServes}
                                onChange={(event) =>
                                  setLines((current) => current.map((candidate) =>
                                    candidate === line
                                      ? { ...candidate, alsoServes: event.target.checked }
                                      : candidate))
                                }
                              />
                            </TableCell>
                          ) : null}
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label={`Remove ${line.name}`}
                              onClick={() =>
                                setLines((current) => current.filter((c) => c !== line))
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )
          })}

        {item.liveCounter ? (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Cooked from — what goes to the venue
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
              <Autocomplete
                size="small"
                options={catalogue?.content ?? []}
                value={ingredientPicked}
                onChange={(_event, value) => setIngredientPicked(value)}
                onInputChange={(_event, value) => setIngredientSearch(value)}
                getOptionLabel={(option) => option.name}
                sx={{ flex: 1, minWidth: 220 }}
                renderInput={(params) => <TextField {...params} label="Item it is made from" />}
              />
              <TextField
                size="small"
                type="number"
                label="Quantity"
                value={ingredientQty}
                onChange={(event) => setIngredientQty(event.target.value)}
                sx={{ width: 120 }}
              />
              <Button
                startIcon={<AddIcon />}
                disabled={!ingredientPicked}
                onClick={() => {
                  if (!ingredientPicked) return
                  setIngredients((current) =>
                    current.some((line) => line.ingredientItemId === ingredientPicked.id)
                      ? current
                      : [...current, {
                          ingredientItemId: ingredientPicked.id,
                          name: ingredientPicked.name,
                          quantity: ingredientQty || '1',
                          unit: 'KG',
                          perGuests: '',
                        }])
                  setIngredientPicked(null)
                  setIngredientQty('1')
                }}
              >
                Add
              </Button>
            </Stack>
            {ingredients.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing mapped.
              </Typography>
            ) : (
              <Table size="small">
                <TableBody>
                  {ingredients.map((line) => (
                    <TableRow key={line.ingredientItemId} hover>
                      <TableCell>{line.name}</TableCell>
                      <TableCell align="right">{formatNumber(Number(line.quantity))}</TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={line.unit}
                          onChange={(event) =>
                            setIngredients((current) => current.map((candidate) =>
                              candidate === line
                                ? { ...candidate, unit: event.target.value }
                                : candidate))
                          }
                          sx={{ width: 100 }}
                        >
                          {['KG', 'LITRE', 'NOS', 'PACKET'].map((unit) => (
                            <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setIngredients((current) => current.filter((c) => c !== line))
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
